import type { CollectionReference, Firestore } from 'firebase/firestore'
import {
  collection,
  collectionGroup,
  doc,
  query,
  where,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { type FirestoreOrganizationSectionPassCoupon, schema } from './schema'
import { getDocsWithError } from '../../firestore-mobx/fetch'
import { convertDocumentSnapshotToModel } from '../../firestore-mobx/stream'
import { OrganizationSectionPassCoupon } from '../../models/OrganizationSectionPassCoupon'
import { type FirebaseRepository } from '../../models/FirebaseRepository'

const converter: FirestoreDataConverter<FirestoreOrganizationSectionPassCoupon> =
  {
    toFirestore: (data) => {
      return data
    },
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return schema.parse(data)
    },
  }

export const getOrganizationSectionPassCouponColRef = (
  firestore: Firestore,
  organizationId: string,
  folderId: string
): CollectionReference<FirestoreOrganizationSectionPassCoupon> => {
  return collection(
    firestore,
    'organization',
    organizationId,
    'section_pass_coupon_folder',
    folderId,
    'section_pass_coupon'
  ).withConverter(converter)
}

export const getOrganizationSectionPassCouponDocRef = (
  firestore: Firestore,
  organizationId: string,
  folderId: string,
  couponId: string
) => {
  return doc(
    getOrganizationSectionPassCouponColRef(firestore, organizationId, folderId),
    couponId
  )
}

export const fetchOrganizationSectionPassCoupons = async ({
  repository,
  organizationId,
  folderId,
  consumed,
}: {
  repository: FirebaseRepository
  organizationId: string
  folderId: string
  consumed: boolean
}) => {
  const couponsColRef = getOrganizationSectionPassCouponColRef(
    repository.firestore,
    organizationId,
    folderId
  ).withConverter(converter)

  const q = query(couponsColRef, where('consumed', '==', consumed))

  const snapshot = await getDocsWithError(
    q,
    'FetchOrganizationSectionPassCouponsError'
  )

  return snapshot.docs.map((doc) =>
    convertDocumentSnapshotToModel(
      repository,
      doc,
      OrganizationSectionPassCoupon
    )
  )
}

/**
 * The organization's codes that were redeemed in one section, across every
 * folder (BL-5134). Each organization code records the section it was used
 * in. Section-level coupons share the collection id but carry no
 * organizationId, so the first filter excludes them. Needs the
 * section_pass_coupon (organizationId, sectionId) collection-group index.
 */
export const fetchOrganizationSectionPassCouponsUsedInSection = async ({
  repository,
  organizationId,
  sectionId,
}: {
  repository: FirebaseRepository
  organizationId: string
  sectionId: string
}) => {
  const q = query(
    collectionGroup(repository.firestore, 'section_pass_coupon').withConverter(
      converter
    ),
    where('organizationId', '==', organizationId),
    where('sectionId', '==', sectionId)
  )

  const snapshot = await getDocsWithError(
    q,
    'FetchOrganizationSectionPassCouponsUsedInSectionError'
  )

  return snapshot.docs.map((doc) =>
    convertDocumentSnapshotToModel(
      repository,
      doc,
      OrganizationSectionPassCoupon
    )
  )
}
